import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    canScheduleTransactionAccountType,
    canScheduleTransactionAmount,
} from "@common/helpers/domains/accounting/scheduleTransactionFit";
import { ApplyScheduleTemplateBulkSource } from "@common/schemas/domains/accounting/scheduleApplication.schema";
import { ScheduleType } from "@common/types/domains/accounting/schedule";

export type BulkScheduleNoun = "transaction" | "invoice" | "schedule";

export interface BulkScheduleRow {
    entityId: number;
    source: ApplyScheduleTemplateBulkSource;
    existingApplicationIds: string[];
    existingTemplateId: string | null;
    hasMatchedSchedule: boolean;
    blockReason: string | null;
    /** Signed bank amount; null for sources without a direction (invoices). */
    transactionAmount: number | null;
    /** The accrual ledger account a transaction sits in; absent for other sources. */
    accountType?: AccountType | null;
}

export interface BulkScheduleItem {
    entityId: number;
    source: ApplyScheduleTemplateBulkSource;
}

export interface BulkSchedulePartition {
    items: BulkScheduleItem[];
    replaceCount: number;
    skippedReasons: string[];
}

export const BULK_SCHEDULE_SKIP_REASONS = {
    zeroAmount: "amount must be greater than zero",
    sameTemplate: "already on this schedule",
    matchedScheduled: "a matched payment or invoice already has a schedule",
    notMoneyIn: "revenue schedules need money-in transactions",
    notMoneyOut: "expense schedules need money-out transactions",
    revenueOnExpenseAccount:
        "revenue schedules can't go on transactions in an expense account",
    expenseOnIncomeAccount:
        "expense schedules can't go on transactions in an income account",
} as const;

/** The one date a bulk apply sends; only a Specific date policy reads it. */
export function resolveBulkScheduleSpecificDate(options: {
    template: { hasSpecificDateAnchor: boolean };
    specificDate: string | null;
}): string | undefined {
    return options.template.hasSpecificDateAnchor && options.specificDate
        ? options.specificDate
        : undefined;
}

export function partitionBulkScheduleRows(options: {
    rows: BulkScheduleRow[];
    template: { id: string; type: ScheduleType };
}): BulkSchedulePartition {
    const { template } = options;
    const items: BulkScheduleItem[] = [];
    const skippedReasons: string[] = [];
    let replaceCount = 0;
    for (const row of options.rows) {
        if (row.blockReason) {
            skippedReasons.push(row.blockReason);
            continue;
        }
        // A zero amount fails the body schema and rejects the whole batch.
        if (row.source.amount <= 0) {
            skippedReasons.push(BULK_SCHEDULE_SKIP_REASONS.zeroAmount);
            continue;
        }
        if (
            row.transactionAmount !== null &&
            !canScheduleTransactionAmount({
                type: template.type,
                amount: row.transactionAmount,
            })
        ) {
            skippedReasons.push(
                template.type === ScheduleType.expense
                    ? BULK_SCHEDULE_SKIP_REASONS.notMoneyOut
                    : BULK_SCHEDULE_SKIP_REASONS.notMoneyIn,
            );
            continue;
        }
        if (
            !canScheduleTransactionAccountType({
                type: template.type,
                accountType: row.accountType ?? null,
            })
        ) {
            skippedReasons.push(
                template.type === ScheduleType.expense
                    ? BULK_SCHEDULE_SKIP_REASONS.expenseOnIncomeAccount
                    : BULK_SCHEDULE_SKIP_REASONS.revenueOnExpenseAccount,
            );
            continue;
        }
        if (row.existingTemplateId === template.id) {
            skippedReasons.push(BULK_SCHEDULE_SKIP_REASONS.sameTemplate);
            continue;
        }
        if (row.existingApplicationIds.length === 0 && row.hasMatchedSchedule) {
            skippedReasons.push(BULK_SCHEDULE_SKIP_REASONS.matchedScheduled);
            continue;
        }
        if (row.existingApplicationIds.length > 0) {
            replaceCount += 1;
            items.push({
                entityId: row.entityId,
                source: {
                    ...row.source,
                    replaceApplicationIds: row.existingApplicationIds,
                },
            });
            continue;
        }
        items.push({ entityId: row.entityId, source: row.source });
    }
    return { items, replaceCount, skippedReasons };
}

/**
 * Why a policy is greyed out for the whole selection: no selected row runs
 * the policy's way. A mixed selection stays enabled and skips per row.
 */
export function getBulkScheduleDirectionBlockReason(options: {
    rows: Array<Pick<BulkScheduleRow, "transactionAmount">>;
    template: { type: ScheduleType };
}): string | null {
    const directedAmounts = options.rows.flatMap((row) =>
        row.transactionAmount === null ? [] : [row.transactionAmount],
    );
    if (
        directedAmounts.length === 0 ||
        directedAmounts.some((amount) =>
            canScheduleTransactionAmount({
                type: options.template.type,
                amount,
            }),
        )
    ) {
        return null;
    }
    return options.template.type === ScheduleType.expense
        ? BULK_SCHEDULE_SKIP_REASONS.notMoneyOut
        : BULK_SCHEDULE_SKIP_REASONS.notMoneyIn;
}

export function filterTemplatesForEntities<
    TTemplate extends { entityIds: number[] },
>(options: { templates: TTemplate[]; entityIds: number[] }): TTemplate[] {
    if (options.entityIds.length === 0) {
        return [];
    }
    return options.templates.filter((template) =>
        options.entityIds.every((entityId) =>
            template.entityIds.includes(entityId),
        ),
    );
}

/** One request per entity: the endpoint takes a single `entityId`. */
export function groupBulkScheduleItemsByEntity(
    items: BulkScheduleItem[],
): Array<{ entityId: number; sources: ApplyScheduleTemplateBulkSource[] }> {
    const byEntity = new Map<number, ApplyScheduleTemplateBulkSource[]>();
    for (const item of items) {
        const sources = byEntity.get(item.entityId) ?? [];
        sources.push(item.source);
        byEntity.set(item.entityId, sources);
    }
    return [...byEntity.entries()].map(([entityId, sources]) => ({
        entityId,
        sources,
    }));
}
